/**
 * Upload first, account after (2026-10-16).
 *
 * /start lets someone upload their bill before they have an account. The
 * server hands back signed upload URLs under pending/<id>/, we upload, and
 * keep the id in localStorage. Once they've verified their email, AppGate
 * calls claimPendingUpload and the server turns it into a new bill_drafts
 * row. Any bills they parked before stay as they are, and the claimed one
 * is their newest, so /case/draft with no id opens it.
 *
 * Since 2026-11-03 the server also attaches uploads by the email typed on
 * them, so AppGate asks once per tab even when this device has no id (the
 * code email opened on another phone, or a link from a follow-up email).
 * See apps/server/src/routes/freeStart.ts.
 */

import { supabase } from './supabase';
import { API_BASE } from './apiConfig';

const KEY = 'ml_pending_upload';
// The phone number from /start (2026-11-02). Kept on the device until the
// upload is claimed, because a Google sign-in leaves the page and comes
// back, and the claim is where it gets saved on their account.
const PHONE_KEY = 'ml_pending_phone';

export function setPendingPhone(phone: string): void {
  try {
    localStorage.setItem(PHONE_KEY, phone);
  } catch {
    /* no localStorage, the bill page asks for it instead */
  }
}

function getPendingPhone(): string | null {
  try {
    return localStorage.getItem(PHONE_KEY);
  } catch {
    return null;
  }
}
// Same as the server's claim window.
const MAX_AGE_MS = 3 * 24 * 3600 * 1000;

export function getPendingUploadId(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { id, at } = JSON.parse(raw) as { id?: string; at?: number };
    if (!id || !at || Date.now() - at > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return id;
  } catch {
    return null;
  }
}

function setPendingUploadId(id: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ id, at: Date.now() }));
  } catch {
    /* no localStorage, the claim just won't happen on this device */
  }
}

export function clearPendingUploadId(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Uploads the bill (first file) and any extra pages without an account.
 * Returns how many files made it, or throws with a message to show.
 */
export async function uploadWithoutAccount(
  files: File[],
  extras: { dueDate?: string | null } = {},
): Promise<{ id: string; uploaded: number }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/free/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        files: files.map((f) => ({ name: f.name, size: f.size })),
        due_date: extras.dueDate || null,
      }),
    });
  } catch {
    throw new Error("We couldn't reach our server. Check your connection and try again.");
  }
  const body = (await res.json().catch(() => ({}))) as {
    id?: string;
    uploads?: { path: string; token: string }[];
    error?: string;
  };
  if (!res.ok || !body.id || !body.uploads) {
    throw new Error(body.error && body.error !== 'off' ? body.error : 'We could not start your upload. Try again in a moment.');
  }

  let uploaded = 0;
  for (let i = 0; i < body.uploads.length; i += 1) {
    const { path, token } = body.uploads[i];
    const { error } = await supabase.storage.from('medical_bills').uploadToSignedUrl(path, token, files[i]);
    if (!error) uploaded += 1;
    // The bill itself has to make it. A missing extra page is just dropped.
    else if (i === 0) throw new Error("Your bill didn't upload. Check your connection and try again.");
  }

  setPendingUploadId(body.id);
  // Tells Flavia a bill came in. Fire and forget.
  void fetch(`${API_BASE}/free/uploaded`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: body.id, files: uploaded }),
  }).catch(() => {});
  return { id: body.id, uploaded };
}

/** Writes the email they typed onto the upload, so we can see who got that far. */
export function notePendingEmail(id: string, email: string): void {
  void fetch(`${API_BASE}/free/pending-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, email }),
  }).catch(() => {});
}

/**
 * After login: attaches a /start upload to the account. Sends the id this
 * device kept, if any. The server also picks up any upload with their email
 * on it. True when there's a bill to open on /case/draft.
 */
export async function claimPendingUpload(accessToken: string): Promise<boolean> {
  const id = getPendingUploadId();
  try {
    const res = await fetch(`${API_BASE}/free/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ id, phone: getPendingPhone() }),
    });
    // Claimed, already claimed, or gone: either way don't try again.
    if (res.ok || res.status === 404 || res.status === 410) {
      clearPendingUploadId();
      try {
        localStorage.removeItem(PHONE_KEY);
      } catch {
        /* nothing to do */
      }
    }
    if (!res.ok) return false;
    const body = (await res.json().catch(() => ({}))) as { claimed?: number; already?: boolean };
    return (body.claimed ?? 0) > 0 || body.already === true;
  } catch {
    return false;
  }
}

/** What the email screen can say about the bill before they sign up. Never the findings. */
export type PeekOutline = {
  provider: string | null;
  amountDue: number | null;
  flagged: number;
  itemized: boolean;
  lineItems: number;
  estimate: { low: number; high: number } | null;
};

export type PeekResult = { kind: 'look'; look: PeekOutline } | { kind: 'not_a_bill' } | null;

/**
 * Reads the bill while they fill in the email screen (/free/peek). Asks
 * again while the read is running, gives up after about a minute. Null
 * means there's nothing to show, the page just carries on without it.
 */
export async function peekPendingUpload(id: string): Promise<PeekResult> {
  const until = Date.now() + 60_000;
  while (Date.now() < until) {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/free/peek`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
    } catch {
      return null;
    }
    if (res.status === 202) {
      await new Promise((r) => setTimeout(r, 3000));
      continue;
    }
    const body = (await res.json().catch(() => ({}))) as Partial<PeekOutline> & { error?: string };
    if (res.ok) {
      return {
        kind: 'look',
        look: {
          provider: body.provider ?? null,
          amountDue: typeof body.amountDue === 'number' ? body.amountDue : null,
          flagged: typeof body.flagged === 'number' ? body.flagged : 0,
          itemized: Boolean(body.itemized),
          lineItems: typeof body.lineItems === 'number' ? body.lineItems : 0,
          estimate: body.estimate ?? null,
        },
      };
    }
    // An EOB on its own or a page with nothing on it is still worth a look
    // by a person, so only a clear "not a bill" gets said on the page.
    if (res.status === 422 && body.error === 'not_a_bill') return { kind: 'not_a_bill' };
    return null;
  }
  return null;
}

/**
 * The phone A/B test on /start (2026-11-03). 'required' is what shipped on
 * 2026-11-02, 'optional' lets them skip it and the bill page asks before
 * the first look instead. Picked once per device, 50/50.
 */
export type StartPhoneVariant = 'required' | 'optional';
const PHONE_AB_KEY = 'ml_ab_start_phone';

export function startPhoneVariant(): StartPhoneVariant {
  try {
    const saved = localStorage.getItem(PHONE_AB_KEY);
    if (saved === 'required' || saved === 'optional') return saved;
    const picked: StartPhoneVariant = Math.random() < 0.5 ? 'required' : 'optional';
    localStorage.setItem(PHONE_AB_KEY, picked);
    return picked;
  } catch {
    // No storage, no consistent split. Keep what shipped.
    return 'required';
  }
}

/**
 * "No bill with you?" (2026-10-26). Saves the email (and the hospital and
 * due date if given) and the server sends the first reminder. Returns an
 * error message to show, or null when it worked.
 */
export async function requestBillReminder(input: {
  email: string;
  provider: string;
  dueDate: string;
}): Promise<string | null> {
  const utm: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const [k, v] of params) if (k.startsWith('utm_')) utm[k] = v;
  } catch {
    /* no UTMs, fine */
  }
  try {
    const res = await fetch(`${API_BASE}/free/remind`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: input.email,
        provider: input.provider || null,
        dueDate: input.dueDate || null,
        page: document.referrer || null,
        utm: Object.keys(utm).length ? utm : null,
      }),
    });
    if (res.ok) return null;
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return body.error ?? 'Something went wrong. Try again in a moment.';
  } catch {
    return "We couldn't reach the server. Check your connection and try again.";
  }
}

/**
 * The link in a reminder email carries ?rt=. Swap it for a session.
 * True when they're now signed in. Any failure just leaves them on /start.
 */
export async function signInWithReminderToken(token: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/free/remind-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) return false;
    const { hashed_token } = (await res.json()) as { hashed_token?: string };
    if (!hashed_token) return false;
    const { error } = await supabase.auth.verifyOtp({ token_hash: hashed_token, type: 'magiclink' });
    return !error;
  } catch {
    return false;
  }
}
